import { without } from 'ramda';
import { isNotEmpty } from 'ramda-adjunct';
import { ChangeRequestField, ObjectTypeAction } from '../../service/changerequestService';
import { addUniqueItemToArray } from './transactionHelpers';

/**
 * Result of building EAN change request
 */
export interface EanChangeRequestResult {
  updatedEans: string[];
  changeRequestFields: NonNullable<ChangeRequestField['fields']>;
}

/**
 * Builds change request fields for EAN modifications
 * Pure function that handles both additions and deletions of EANs
 *
 * @param currentEans - Current EAN array from the product
 * @param newEan - New EAN to add (empty string if none)
 * @param eansToDelete - Array of EANs to remove
 * @returns Updated EAN array and change request fields
 */
export function buildEanChangeRequest(
  currentEans: string[],
  newEan: string,
  eansToDelete: string[]
): EanChangeRequestResult {
  // Remove deleted EANs
  const eansAfterDeletion = without(eansToDelete, currentEans);

  // Build change request fields for deletions
  const changeRequestFields: ChangeRequestField['fields'] = eansToDelete.map((ean) => ({
    name: 'ean',
    action: ObjectTypeAction.REMOVE,
    value: ean.trim(),
    currentValue: currentEans,
  }));

  // Add new EAN if valid and unique
  const updatedEans = addUniqueItemToArray(eansAfterDeletion, newEan, (ean) => ean.trim());

  // If a new EAN was actually added, create an ADD change request
  if (updatedEans.length > eansAfterDeletion.length && isNotEmpty(newEan)) {
    changeRequestFields.push({
      name: 'ean',
      action: ObjectTypeAction.ADD,
      value: newEan.trim(),
      currentValue: currentEans,
    });
  }

  return {
    updatedEans,
    changeRequestFields,
  };
}
